import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/UI/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/UI/dropdown-menu';
import { Calendar } from '../components/UI/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import HoverableItem from '../components/project_overview/project_overview_table_hoverable_item';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { TruncatedTooltipText } from '../components/ui/truncated-tooltip-text';
import { deleteEstimate, getAllEstimates, updateEstimate } from '../api/estimates';
import {
  exportEstimateFromBuilder,
  QUICKBOOKS_BUNDLE_SETUP_REQUIRED_EVENT,
  quickbooksCheckIsConnected,
} from '../api/quickbooks';
import {
  isQuickBooksBundleSetupRequired,
  isQuickBooksOnlineError,
  showQuickBooksOnlineErrorToast,
} from '../helper/quickBooksNotifications';
import type { ProjectEstimate } from '../interfaces/project-estimate';
import QuickBook from '../assets/icons/QB.svg';
import EstimateBuilderSheet from '../components/estimating/EstimateBuilderSheet';
import EstimateModal from '../modals/estimate_modal';
import BundleSetupModal from '../modals/bundle_setup_modal';
import {
  ChevronDown,
  Eye,
  ExternalLink,
  Loader2,
  MoreHorizontal,
  Info,
  Pencil,
  // Plus,
  Send,
  Settings,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import EstimateHistory from '../components/estimating/EstimateHistory';
import { createEstimateHistory, EstimateHistoryPayloadType } from '../api/estimate-history';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip-shadcn';
import { trackView } from '../components/analytics/track_view';
import { MixpanelEvents } from '../components/analytics/mix_panel_events';

type BuilderMode = 'create' | 'view' | 'edit';
type SendDialogMode = 'send' | 'saveAndSend';

type ClientEstimatingProps = {
  clientId?: number;
  accountId?: number;
  client?: any;
  account?: any;
  accountUser?: any;
  singleUser?: boolean;
  qboConnected?: boolean;
  onChanged?: () => void | Promise<void>;
};

type BuilderState = {
  open: boolean;
  mode: BuilderMode;
  estimateId: number | null;
  initialDialog: SendDialogMode | null;
};

type DatePopoverState = {
  rowId: number | null;
  field: 'createdAt' | 'invoiceDate' | null;
};

const ESTIMATE_STATUS_OPTIONS = ['Created', 'Sent to Customer', 'Exported to QB'];
const INVOICE_STATUS_OPTIONS = ['Not Created', 'Paid', 'Partial', 'Not Paid'];
const LAST_UPDATE_NOTE_PREVIEW_LIMIT = 140;

const normalizeEstimateTaxSelection = (value?: string | null) => {
  if (value === 'excluded') return 'exclude';
  if (value === 'calculated') return 'calculate';
  return value || 'exclude';
};

const getFilledStatus = (value?: string | null) =>
  String(value || '').trim() ? 'filled' : 'empty';

const resolveErrorMessage = (error: any, fallback: string) =>
  String(error?.response?.data?.message || error?.message || fallback);

const buildEstimateActionTrackingProps = (
  estimate: ProjectEstimate,
  extra: Record<string, any> = {}
) => {
  const body = estimate?.estimateBody || {};
  return {
    tax_selection: normalizeEstimateTaxSelection(
      body?.tax?.mode || body?.taxMode || body?.preview?.taxMode
    ),
    estimate_expiration: body?.expirationDate || '',
    payment_terms: getFilledStatus(body?.paymentTerms),
    terms_and_conditions: getFilledStatus(body?.termsAndConditions),
    ...extra,
  };
};

const qbBaseUrl = () => {
  if (typeof window === 'undefined') {
    return 'https://app.qbo.intuit.com';
  }
  const host = String(window.location.hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'https://sandbox.qbo.intuit.com';
  }
  const env = (localStorage.getItem('qboEnv') || '').toLowerCase();
  return env === 'sandbox' ? 'https://sandbox.qbo.intuit.com' : 'https://app.qbo.intuit.com';
};

const buildQboEstimateUrl = (estimateId?: string | null) => {
  if (!estimateId) return '';
  return `${qbBaseUrl()}/app/estimate?txnId=${encodeURIComponent(estimateId)}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : '—';
};

const toInvoiceDateValue = (value: Date | null | undefined) => {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const toCalendarDate = (value?: string | null) => {
  if (!value) return undefined;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.toDate() : undefined;
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value));
};

const resolveEstimateParts = (estimate: ProjectEstimate) => {
  const body = estimate?.estimateBody || {};
  const rawVersioned =
    body?.versionedNumber || body?.estimateNumber || (estimate as any)?.versionedNumber || '';
  const rawBase = body?.baseNumber || (estimate as any)?.baseNumber || '';
  const explicitVersion = Number(body?.version || (estimate as any)?.version || 0);

  if (rawVersioned) {
    const match = String(rawVersioned).match(/^(.*?)-([0-9]{2,})$/);
    if (match) {
      return {
        baseNumber: String(match[1] || '').trim(),
        versionedNumber: String(rawVersioned),
        version: Number(match[2]) || explicitVersion || 1,
      };
    }
  }

  if (rawBase) {
    const version = explicitVersion || 1;
    return {
      baseNumber: String(rawBase),
      versionedNumber: `${rawBase}-${String(version).padStart(2, '0')}`,
      version,
    };
  }

  return {
    baseNumber: `EST-${estimate.id}`,
    versionedNumber: `EST-${estimate.id}`,
    version: explicitVersion || 1,
  };
};

const resolveEstimateNumber = (estimate: ProjectEstimate) =>
  resolveEstimateParts(estimate).versionedNumber;

const resolveEstimateInternalName = (estimate: ProjectEstimate) => {
  const body = estimate?.estimateBody || {};
  const value = body?.internalName || body?.name || body?.title || '';
  return String(value || '').trim();
};

const resolveQuickBooksDate = (estimate: ProjectEstimate) => {
  if (estimate?.qbExportedAt) return formatDate(estimate.qbExportedAt);
  if (estimate?.estimateBody?.qbExportedAt) return formatDate(estimate.estimateBody.qbExportedAt);
  return '';
};

const QuickBooksConnectTooltip = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        className="pointer-events-auto ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <Info className="h-4 w-4" />
      </span>
    </TooltipTrigger>
    <TooltipContent
      side="left"
      sideOffset={8}
      className="z-[99999999] bg-white text-[#545454] shadow-lg"
    >
      <span className="text-xs">
        <Link to="/quickbooks_connect" className="text-[#3B97B1] underline">
          Link
        </Link>{' '}
        your QuickBooks account to enable exporting
      </span>
    </TooltipContent>
  </Tooltip>
);

const resolveStatusClassName = (status?: string | null) => {
  const normalized = String(status || 'Created').toLowerCase();
  if (normalized.includes('exported')) return 'text-[#A56909]';
  if (normalized.includes('created')) return 'text-[#2563EB]';
  if (normalized.includes('sent')) return 'text-[#15803d]';
  return 'text-foreground';
};

const resolveInvoiceStatusClassName = (status?: string | null) => {
  const normalized = String(status || 'Not Created')
    .trim()
    .toLowerCase();
  if (normalized === 'paid') return 'invoicePaid_paid';
  if (normalized === 'partial') return 'invoicePaid_partial';
  if (normalized === 'not paid') return 'invoicePaid_not_paid';
  return 'invoicePaid_not_created';
};

export type ClientEstimatingRef = {
  openBuilder: (
    mode: BuilderMode,
    estimate?: ProjectEstimate | null,
    initialDialog?: SendDialogMode | null,
  ) => void;
};

const ClientEstimating = forwardRef<ClientEstimatingRef, ClientEstimatingProps>(
  (
    {
      clientId,
      accountId,
      client,
      account,
      accountUser,
      singleUser,
      qboConnected: qboConnectedProp,
      onChanged,
    },
    ref
  ) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
    const [qboConnected, setQboConnected] = useState<boolean>(qboConnectedProp === true);
    const [qboConnectionChecked, setQboConnectionChecked] = useState<boolean>(
      typeof qboConnectedProp === 'boolean'
    );
    const [rows, setRows] = useState<ProjectEstimate[]>([]);
    const [deleteTarget, setDeleteTarget] = useState<ProjectEstimate | null>(null);
    const [savingInvoiceIds, setSavingInvoiceIds] = useState<number[]>([]);
    const [datePopover, setDatePopover] = useState<DatePopoverState>({
      rowId: null,
      field: null,
    });
    const [activeInvoiceDropdownId, setActiveInvoiceDropdownId] = useState<number | null>(null);
    const [invoiceDropdownStyle, setInvoiceDropdownStyle] = useState<React.CSSProperties | null>(
      null
    );
    const [invoiceNumberOpenId, setInvoiceNumberOpenId] = useState<number | null>(null);
    const [invoiceNumberDrafts, setInvoiceNumberDrafts] = useState<Record<number, string>>({});
    const [expandedNoteIds, setExpandedNoteIds] = useState<Record<number, boolean>>({});
    const [exportSuccessModalOpen, setExportSuccessModalOpen] = useState(false);
    const [exportedQbEstimateId, setExportedQbEstimateId] = useState('');
    const [exportedQbEstimateDocNumber, setExportedQbEstimateDocNumber] = useState('');
    const [bundleSetupModalOpen, setBundleSetupModalOpen] = useState(false);
    const [bundleSetupMissingItems, setBundleSetupMissingItems] = useState<string[]>([]);
    const [bundleSetupPendingEstimate, setBundleSetupPendingEstimate] =
      useState<ProjectEstimate | null>(null);
    const bundleSetupPendingEstimateRef = useRef<ProjectEstimate | null>(null);
    const [builderState, setBuilderState] = useState<BuilderState>({
      open: false,
      mode: 'view',
      estimateId: null,
      initialDialog: null,
    });
    const invoiceDropdownRef = useRef<HTMLUListElement | null>(null);
    const invoiceTriggerRefs = useRef<Record<number, HTMLDivElement | null>>({});

    const loadEstimates = useCallback(async () => {
      if (!clientId) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await getAllEstimates({ clientId, accountId });
        const estimates = Array.isArray(response) ? response : [];
        setRows(estimates as ProjectEstimate[]);
      } catch (error) {
        console.error('Failed to load estimates:', error);
        toast.error('Failed to load estimates');
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, [accountId, clientId]);

    const handleEstimatingChanged = useCallback(async () => {
      await loadEstimates();
      if (typeof onChanged === 'function') {
        await onChanged();
      }
    }, [loadEstimates, onChanged]);

    useEffect(() => {
      void loadEstimates();
    }, [loadEstimates]);

    useEffect(() => {
      if (typeof qboConnectedProp === 'boolean') {
        setQboConnected(qboConnectedProp);
        setQboConnectionChecked(true);
        return;
      }

      if (!accountId) {
        setQboConnected(false);
        setQboConnectionChecked(true);
        return;
      }

      let active = true;
      setQboConnectionChecked(false);
      quickbooksCheckIsConnected(Number(accountId || 0))
        .then((response) => {
          if (!active) return;
          setQboConnected(response?.connected === true && !!response?.userinfo);
        })
        .catch((error) => {
          console.error('Failed to check QuickBooks connection:', error);
          if (active) setQboConnected(false);
        })
        .finally(() => {
          if (active) setQboConnectionChecked(true);
        });

      return () => {
        active = false;
      };
    }, [accountId, qboConnectedProp]);

    const sortedRows = useMemo(() => {
      return [...rows].sort((a, b) => {
        const at = new Date(a?.createdAt || '').getTime();
        const bt = new Date(b?.createdAt || '').getTime();
        return bt - at;
      });
    }, [rows]);

    useEffect(() => {
      if (activeInvoiceDropdownId === null) {
        setInvoiceDropdownStyle(null);
        return;
      }

      const anchorEl = invoiceTriggerRefs.current[activeInvoiceDropdownId];
      if (!anchorEl) return;

      const updatePosition = () => {
        const rect = anchorEl.getBoundingClientRect();
        const width = 150;
        const height = 220;
        const gap = 6;
        const shouldDropUp =
          rect.bottom + height + gap > window.innerHeight && rect.top - height - gap > 0;
        const top = shouldDropUp ? rect.top - height - gap : rect.bottom + gap;

        setInvoiceDropdownStyle({
          position: 'fixed',
          top: Math.min(Math.max(8, top), window.innerHeight - height - 8),
          left: rect.left,
          width,
          zIndex: 3000,
        });
      };

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const dropdownEl = invoiceDropdownRef.current;
        if (anchorEl.contains(target)) return;
        if (dropdownEl && dropdownEl.contains(target)) return;
        setActiveInvoiceDropdownId(null);
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('mousedown', handleClickOutside);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('mousedown', handleClickOutside);
      };
    }, [activeInvoiceDropdownId]);

    const patchRow = useCallback((estimateId: number, patch: Partial<ProjectEstimate>) => {
      setRows((prev) => prev.map((row) => (row.id === estimateId ? { ...row, ...patch } : row)));
    }, []);

    const saveInvoicePatch = useCallback(
      async (estimate: ProjectEstimate, patch: Partial<ProjectEstimate>) => {
        const previous = Object.keys(patch).reduce((all, key) => {
          (all as any)[key] = (estimate as any)[key] ?? null;
          return all;
        }, {} as Partial<ProjectEstimate>);

        patchRow(estimate.id, patch);
        setSavingInvoiceIds((prev) => [...new Set([...prev, estimate.id])]);

        try {
          await updateEstimate({
            accountId,
            estimateId: estimate.id,
            patch,
          });
        } catch (error) {
          console.error('Failed to update estimate fields:', error);
          patchRow(estimate.id, previous);
          toast.error('Failed to update estimate details');
        } finally {
          setSavingInvoiceIds((prev) => prev.filter((id) => id !== estimate.id));
        }
      },
      [accountId, patchRow]
    );

    const openInvoiceNumberEditor = useCallback((estimate: ProjectEstimate) => {
      setInvoiceNumberDrafts((prev) => ({
        ...prev,
        [estimate.id]: estimate.invoiceNumber || '',
      }));
      setInvoiceNumberOpenId(estimate.id);
      setActiveInvoiceDropdownId(null);
    }, []);

    const commitInvoiceNumber = useCallback(
      async (estimate: ProjectEstimate) => {
        const nextValue = String(invoiceNumberDrafts[estimate.id] ?? estimate.invoiceNumber ?? '')
          .replace(/^#/, '')
          .trim();
        const previousValue = String(estimate.invoiceNumber || '').trim();
        setInvoiceNumberOpenId(null);
        if (nextValue === previousValue) return;
        await saveInvoicePatch(estimate, {
          invoiceNumber: nextValue || null,
        });
      },
      [invoiceNumberDrafts, saveInvoicePatch]
    );

    const openBuilder = useCallback(
      (
        mode: BuilderMode,
        estimate?: ProjectEstimate | null,
        initialDialog: SendDialogMode | null = null
      ) => {
        setBuilderState({
          open: true,
          mode,
          estimateId: estimate?.id || null,
          initialDialog,
        });
      },
      []
    );

    useImperativeHandle(ref, () => ({
      openBuilder: () => openBuilder('create'),
    }));

    const closeBuilder = useCallback(() => {
      setBuilderState((prev) => ({ ...prev, open: false, initialDialog: null }));
    }, []);

    const handleView = (estimate: ProjectEstimate) => openBuilder('view', estimate);
    const handleSendUpdate = (estimate: ProjectEstimate) => openBuilder('view', estimate, 'send');
    const handleEdit = (estimate: ProjectEstimate) => openBuilder('edit', estimate);

    const handleExportQuickBooks = useCallback(
      async (estimate: ProjectEstimate, forceNoBundling = false) => {
        const selectedItemIds = Array.isArray(estimate.items) ? estimate.items : [];
        const selectedFeeIds = Array.isArray(estimate?.estimateBody?.selectedFeeIds)
          ? estimate.estimateBody.selectedFeeIds
          : [];

        if (!selectedItemIds.length && !selectedFeeIds.length) {
          trackView(
            MixpanelEvents.ESTIMATE_QB,
            buildEstimateActionTrackingProps(estimate, {
              result: 'error',
              fe_error_text: 'This estimate has no selected items.',
            })
          );
          toast.error('This estimate has no selected items.');
          return;
        }

        bundleSetupPendingEstimateRef.current = estimate;
        setBundleSetupPendingEstimate(estimate);
        setActionLoadingId(estimate.id);
        try {
          const exportRes = await exportEstimateFromBuilder({
            accountId: Number(accountId || 0),
            estimateId: estimate.id,
            forceNoBundling,
          });

          if (exportRes?.error) {
            const message = exportRes.message || 'Failed to export estimate to QuickBooks';
            trackView(
              MixpanelEvents.ESTIMATE_QB,
              buildEstimateActionTrackingProps(estimate, {
                result: 'error',
                be_error_text: message,
              })
            );
            if (isQuickBooksBundleSetupRequired(exportRes)) {
              setBundleSetupMissingItems(exportRes.missingBundleItems || []);
              setBundleSetupPendingEstimate(estimate);
              setBundleSetupModalOpen(true);
              return;
            }
            bundleSetupPendingEstimateRef.current = null;
            setBundleSetupPendingEstimate(null);
            const onRetry = () => void handleExportQuickBooks(estimate);
            if (isQuickBooksOnlineError(exportRes)) {
              showQuickBooksOnlineErrorToast({
                message,
                onRetry,
                onBundleSetupRequired: () => {
                  setBundleSetupMissingItems(exportRes.missingBundleItems || []);
                  setBundleSetupPendingEstimate(estimate);
                  setBundleSetupModalOpen(true);
                },
              });
            } else {
              toast.error(message, {
                duration: Infinity,
                action: {
                  label: 'Retry',
                  onClick: onRetry,
                },
              });
            }
            return;
          }

          const exportedAt = exportRes?.qbExportedAt || new Date().toISOString();
          const exportedEstimateId = String(
            exportRes?.estimateId || exportRes?.result?.Estimate?.Id || ''
          );
          const exportedEstimateDocNumber = String(exportRes?.result?.Estimate?.DocNumber || '');

          await updateEstimate({
            accountId,
            estimateId: estimate.id,
            patch: {
              status: 'Exported to QB',
              qbEstimateId: exportRes?.estimateId || exportRes?.result?.Estimate?.Id || null,
              qbEstimateNumber: exportRes?.result?.Estimate?.DocNumber || null,
              qbExportedAt: exportedAt,
            },
          });
          toast.success(
            estimate?.qbEstimateId
              ? 'Estimate updated in QuickBooks'
              : 'Estimate exported to QuickBooks'
          );
          // Create History
          let historyPayload: EstimateHistoryPayloadType = {
            estimateId: estimate.id,
            userId: accountUser?.id || null,
            accountId: accountId || null,
            userLabel: !singleUser ? accountUser?.label || null : null,
            internalNote: estimate?.note || null,
            versionedNumber: estimate?.estimateBody?.versionedNumber || null,
            action: 'exported_to_qb',
            qbEstimateNumber: exportRes?.result?.Estimate?.DocNumber || null,
          };
          const historyRes = await createEstimateHistory(historyPayload);
          if (!historyRes) {
            toast.error('Failed to create history.');
          }

          trackView(
            MixpanelEvents.ESTIMATE_QB,
            buildEstimateActionTrackingProps(estimate, {
              result: 'success',
            })
          );

          if (exportedEstimateId) {
            setExportedQbEstimateId(exportedEstimateId);
            setExportedQbEstimateDocNumber(exportedEstimateDocNumber);
            setExportSuccessModalOpen(true);
          }

          await handleEstimatingChanged();
          bundleSetupPendingEstimateRef.current = null;
          setBundleSetupPendingEstimate(null);
        } catch (error: any) {
          bundleSetupPendingEstimateRef.current = null;
          setBundleSetupPendingEstimate(null);
          console.error('Failed to export estimate to QuickBooks:', error);
          const message = resolveErrorMessage(error, 'Failed to export estimate to QuickBooks');
          trackView(
            MixpanelEvents.ESTIMATE_QB,
            buildEstimateActionTrackingProps(estimate, {
              result: 'error',
              be_error_text: message,
            })
          );
          toast.error(message, {
            duration: Infinity,
            action: {
              label: 'Retry',
              onClick: () => void handleExportQuickBooks(estimate),
            },
          });
        } finally {
          setActionLoadingId(null);
        }
      },
      [
        accountId,
        accountUser?.id,
        accountUser?.label,
        handleEstimatingChanged,
        singleUser,
      ]
    );

    useEffect(() => {
      const handleBundleSetupRequired = (event: Event) => {
        const pendingEstimate = bundleSetupPendingEstimateRef.current;
        if (!pendingEstimate) return;

        const detail = (event as CustomEvent)?.detail || {};
        setBundleSetupMissingItems(detail?.missingBundleItems || []);
        setBundleSetupPendingEstimate(pendingEstimate);
        setBundleSetupModalOpen(true);
      };

      window.addEventListener(
        QUICKBOOKS_BUNDLE_SETUP_REQUIRED_EVENT,
        handleBundleSetupRequired
      );

      return () => {
        window.removeEventListener(
          QUICKBOOKS_BUNDLE_SETUP_REQUIRED_EVENT,
          handleBundleSetupRequired
        );
      };
    }, []);

    const handleCloseBundleSetupModal = useCallback(() => {
      setBundleSetupModalOpen(false);
      setBundleSetupMissingItems([]);
      setBundleSetupPendingEstimate(null);
      bundleSetupPendingEstimateRef.current = null;
    }, []);

    const handleRetryBundleExport = useCallback(() => {
      if (!bundleSetupPendingEstimate) {
        handleCloseBundleSetupModal();
        return;
      }
      const pendingEstimate = bundleSetupPendingEstimate;
      handleCloseBundleSetupModal();
      void handleExportQuickBooks(pendingEstimate);
    }, [bundleSetupPendingEstimate, handleCloseBundleSetupModal, handleExportQuickBooks]);

    const handleExportWithoutBundling = useCallback(() => {
      if (!bundleSetupPendingEstimate) {
        handleCloseBundleSetupModal();
        return;
      }
      const pendingEstimate = bundleSetupPendingEstimate;
      handleCloseBundleSetupModal();
      void handleExportQuickBooks(pendingEstimate, true);
    }, [bundleSetupPendingEstimate, handleCloseBundleSetupModal, handleExportQuickBooks]);

    const handleDeleteConfirm = useCallback(async () => {
      if (!deleteTarget) return;
      setActionLoadingId(deleteTarget.id);
      try {
        await deleteEstimate({ accountId, estimateId: deleteTarget.id });
        toast.success('Estimate deleted');
        setDeleteTarget(null);
        await handleEstimatingChanged();
      } catch (error) {
        console.error('Failed to delete estimate:', error);
        toast.error('Failed to delete estimate');
      } finally {
        setActionLoadingId(null);
      }
    }, [accountId, deleteTarget, handleEstimatingChanged]);

    return (
      <>
        <div className="w-full">
          <div className="mb-0 flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center gap-4">
              <h2 className="m-0 text-[22px] font-semibold leading-[1.2] tracking-tight">
                Estimates
              </h2>
              <Link
                to="/account_settings?tab=estimate"
                className="inline-flex items-center gap-1 text-[14px] text-muted-foreground hover:text-foreground"
              >
                <Settings className="h-4 w-4" />
                Settings
              </Link>
            </div>
            {/* <Button
              type="button"
              className="inline-flex items-center gap-2 bg-[#3B97B1] hover:bg-[#337f95]"
              onClick={() => openBuilder('create')}
            >
              <Plus className="h-4 w-4" />
              New Estimate
            </Button> */}
          </div>

          <div className="w-full">
            <Table showTopScrollbar className="min-w-[1260px] table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[260px] min-w-[260px] max-w-[260px] pl-4 pr-3">
                    Estimate
                  </TableHead>
                  <TableHead className="w-[110px] min-w-[110px] max-w-[110px] px-3 text-right">
                    Amount
                  </TableHead>
                  <TableHead className="min-w-[130px] px-3">Status</TableHead>
                  <TableHead className="min-w-[120px] px-3">Created</TableHead>
                  <TableHead className="min-w-[120px] px-3">Last Sent</TableHead>
                  <TableHead className="w-[190px] min-w-[190px] max-w-[190px] px-3">
                    Last Update Note
                  </TableHead>
                  <TableHead className="min-w-[285px] px-3">Invoice</TableHead>
                  <TableHead className="min-w-[150px] px-3">QuickBooks</TableHead>
                  <TableHead className="min-w-[70px] last:w-[70px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                      <div className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading estimates...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                      No estimates yet
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((estimate) => {
                    const estimateNumber = resolveEstimateNumber(estimate);
                    const internalName = resolveEstimateInternalName(estimate);
                    const qbUrl = buildQboEstimateUrl(estimate?.qbEstimateId);
                    const qbDate = resolveQuickBooksDate(estimate);
                    const noteText = String(estimate?.note || '').trim();
                    const hasNote = noteText.length > 0;
                    const isNoteCollapsible = noteText.length > LAST_UPDATE_NOTE_PREVIEW_LIMIT;
                    const isNoteExpanded = !!expandedNoteIds[estimate.id];
                    const notePreviewText = isNoteCollapsible
                      ? `${noteText.slice(0, LAST_UPDATE_NOTE_PREVIEW_LIMIT).trimEnd()}...`
                      : noteText;
                    const isActionLoading = actionLoadingId === estimate.id;
                    const isInvoiceSaving = savingInvoiceIds.includes(estimate.id);
                    const invoiceStatusValue = estimate?.invoiceStatus || 'Not Created';
                    const invoiceDateValue = estimate?.invoiceDate
                      ? moment(estimate.invoiceDate).format('MM/DD/YY')
                      : null;
                    const invoiceNumberDraft = invoiceNumberDrafts[estimate.id] ?? '';
                    const isEditingInvoiceNumber = invoiceNumberOpenId === estimate.id;

                    return (
                      <TableRow
                        key={estimate.id}
                        data-qa={`client-estimating-row-${estimate.id}`}
                        className="cursor-pointer"
                        onClick={() => handleView(estimate)}
                        data-qa={`client-estimate-row-${estimate.id}`}
                      >
                        <TableCell
                          className={`w-[260px] max-w-[260px] pl-4 pr-3 ${internalName ? 'align-top' : 'align-middle'}`}
                          data-qa={`client-estimate-row-${estimate.id}-number`}
                        >
                          <TruncatedTooltipText
                            text={estimateNumber}
                            className="max-w-[230px] font-semibold text-[#3B97B1] hover:text-[#51a6c5]"
                            tooltipClassName="z-[99999999]"
                          />
                          {internalName ? (
                            <TruncatedTooltipText
                              text={internalName}
                              lines={2}
                              className="max-w-[230px] text-sm leading-4 text-muted-foreground"
                            />
                          ) : null}
                        </TableCell>

                        <TableCell className="w-[110px] min-w-[110px] max-w-[110px] px-3 text-right font-medium">
                          {formatCurrency(estimate?.amount)}
                        </TableCell>

                        <TableCell className="px-3" onClick={(event) => event.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm"
                                disabled={isInvoiceSaving}
                                data-qa={`client-estimate-row-${estimate.id}-status-trigger`}
                              >
                                <span className={resolveStatusClassName(estimate?.status)}>
                                  {estimate?.status || 'Created'}
                                </span>
                                {isInvoiceSaving ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )}
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-[180px]">
                              {ESTIMATE_STATUS_OPTIONS.map((status) => (
                                <DropdownMenuItem
                                  key={`${estimate.id}-estimate-status-${status}`}
                                  onSelect={() => {
                                    if (status === estimate.status) return;
                                    void saveInvoicePatch(estimate, { status });
                                  }}
                                >
                                  <span className={resolveStatusClassName(status)}>{status}</span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>

                        <TableCell className="px-3" onClick={(event) => event.stopPropagation()}>
                          <Popover
                            open={
                              datePopover.rowId === estimate.id && datePopover.field === 'createdAt'
                            }
                            onOpenChange={(open) => {
                              if (isInvoiceSaving) return;
                              setDatePopover({
                                rowId: open ? estimate.id : null,
                                field: open ? 'createdAt' : null,
                              });
                            }}
                          >
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="rounded px-1 py-0.5 text-left hover:bg-muted"
                                disabled={isInvoiceSaving}
                              >
                                {formatDate(estimate?.createdAt)}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="start"
                              className="w-auto p-0"
                              onOpenAutoFocus={(event) => event.preventDefault()}
                            >
                              <Calendar
                                mode="single"
                                selected={toCalendarDate(estimate?.createdAt)}
                                onSelect={(value) => {
                                  setDatePopover({ rowId: null, field: null });
                                  if (!value) return;
                                  const nextValue = moment(value).toISOString();
                                  const previousValue = estimate?.createdAt
                                    ? moment(estimate.createdAt).toISOString()
                                    : null;
                                  if (nextValue === previousValue) return;
                                  void saveInvoicePatch(estimate, { createdAt: nextValue } as any);
                                }}
                                className="rounded-md border-0 p-[12px]"
                              />
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                        <TableCell className="px-3">{formatDate(estimate?.lastSent)}</TableCell>

                        <TableCell
                          className="w-[190px] max-w-[190px] px-3 items-center whitespace-normal break-words"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {hasNote ? (
                            <div className="text-muted-foreground leading-5">
                              <button
                                type="button"
                                className={`block w-full min-w-0 max-w-full text-left ${isNoteCollapsible ? 'cursor-pointer' : 'cursor-default'} ${isNoteExpanded ? 'whitespace-pre-wrap break-all' : 'whitespace-normal break-words'}`}
                                style={
                                  isNoteExpanded
                                    ? undefined
                                    : {
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        lineHeight: '1.25rem',
                                        maxHeight: '3.75rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }
                                }
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (!isNoteCollapsible) return;
                                  setExpandedNoteIds((prev) => ({
                                    ...prev,
                                    [estimate.id]: !prev[estimate.id],
                                  }));
                                }}
                              >
                                {isNoteExpanded ? noteText : notePreviewText}
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        <TableCell
                          className="inlinePaid min-w-[285px] px-3 align-top"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <div className="flex w-full flex-col">
                            <div
                              ref={(node) => {
                                invoiceTriggerRefs.current[estimate.id] = node;
                              }}
                            >
                              <HoverableItem
                                className="editInlineField paddings leftAlign status-wrap-span"
                                handleSetRowId={() => {
                                  if (isInvoiceSaving) return;
                                  setInvoiceNumberOpenId(null);
                                  setActiveInvoiceDropdownId((prev) =>
                                    prev === estimate.id ? null : estimate.id
                                  );
                                }}
                              >
                                <span className="status-line">
                                  <span
                                    style={{ fontSize: '14px' }}
                                    className={resolveInvoiceStatusClassName(invoiceStatusValue)}
                                  >
                                    {invoiceStatusValue}
                                  </span>
                                  {isInvoiceSaving ? (
                                    <Loader2 className="ml-1 h-4 w-4 animate-spin text-muted-foreground" />
                                  ) : (
                                    <ChevronDown size={16} className="inlinePaidArrow" />
                                  )}
                                </span>
                              </HoverableItem>
                            </div>

                            {activeInvoiceDropdownId === estimate.id &&
                              invoiceDropdownStyle &&
                              typeof document !== 'undefined' &&
                              createPortal(
                                <ul
                                  ref={invoiceDropdownRef}
                                  className="inlinePaidDropdown"
                                  style={invoiceDropdownStyle}
                                >
                                  {INVOICE_STATUS_OPTIONS.map((status) => (
                                    <li
                                      key={`${estimate.id}-invoice-status-${status}`}
                                      className={resolveInvoiceStatusClassName(status)}
                                      onClick={() => {
                                        setActiveInvoiceDropdownId(null);
                                        void saveInvoicePatch(estimate, { invoiceStatus: status });
                                      }}
                                    >
                                      {status}
                                    </li>
                                  ))}
                                </ul>,
                                document.body
                              )}

                            {isEditingInvoiceNumber ? (
                              <Input
                                placeholder="#"
                                type="text"
                                autoFocus
                                disabled={isInvoiceSaving}
                                value={invoiceNumberDraft}
                                onChange={(event) => {
                                  setInvoiceNumberDrafts((prev) => ({
                                    ...prev,
                                    [estimate.id]: event.target.value.replace(/^#/, ''),
                                  }));
                                }}
                                onBlur={() => {
                                  void commitInvoiceNumber(estimate);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.currentTarget.blur();
                                  }
                                  if (event.key === 'Escape') {
                                    setInvoiceNumberOpenId(null);
                                    setInvoiceNumberDrafts((prev) => ({
                                      ...prev,
                                      [estimate.id]: estimate.invoiceNumber || '',
                                    }));
                                  }
                                }}
                                className="h-4 min-h-0 rounded-none border-0 bg-transparent p-0 text-[13px] leading-4 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                              />
                            ) : (
                              <HoverableItem
                                className="paddingLeft leftAlign"
                                handleSetRowId={() => {
                                  if (isInvoiceSaving) return;
                                  openInvoiceNumberEditor(estimate);
                                }}
                              >
                                {estimate?.invoiceNumber ? (
                                  `#${estimate.invoiceNumber}`
                                ) : (
                                  <span className="hoverInline">#_</span>
                                )}
                              </HoverableItem>
                            )}

                            <Popover
                              open={
                                datePopover.rowId === estimate.id &&
                                datePopover.field === 'invoiceDate'
                              }
                              onOpenChange={(open) => {
                                if (isInvoiceSaving) return;
                                setDatePopover({
                                  rowId: open ? estimate.id : null,
                                  field: open ? 'invoiceDate' : null,
                                });
                                if (open) {
                                  setActiveInvoiceDropdownId(null);
                                  setInvoiceNumberOpenId(null);
                                }
                              }}
                            >
                              <PopoverTrigger asChild>
                                <div>
                                  <HoverableItem
                                    className="editInlineField leftAlign status-wrap-span"
                                    handleSetRowId={() => {
                                      if (isInvoiceSaving) return;
                                      setActiveInvoiceDropdownId(null);
                                      setInvoiceNumberOpenId(null);
                                    }}
                                  >
                                    <span className="group/date inline-flex items-center gap-1">
                                      {invoiceDateValue ? (
                                        <span>{invoiceDateValue}</span>
                                      ) : (
                                        <span className="hoverInline">MM/DD/YY</span>
                                      )}
                                      {!!invoiceDateValue && !isInvoiceSaving && (
                                        <button
                                          type="button"
                                          aria-label="Clear invoice date"
                                          className="inline-flex h-3.5 w-3.5 items-center justify-center rounded text-[#111827] opacity-0 transition-opacity group-hover/date:opacity-100 hover:text-rose-600"
                                          onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setDatePopover({ rowId: null, field: null });
                                            void saveInvoicePatch(estimate, { invoiceDate: null });
                                          }}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      )}
                                    </span>
                                  </HoverableItem>
                                </div>
                              </PopoverTrigger>
                              <PopoverContent
                                align="start"
                                className="w-auto p-0"
                                onOpenAutoFocus={(event) => event.preventDefault()}
                              >
                                <Calendar
                                  mode="single"
                                  selected={toCalendarDate(estimate?.invoiceDate)}
                                  onSelect={(value) => {
                                    setDatePopover({ rowId: null, field: null });
                                    const nextValue = toInvoiceDateValue(value || null);
                                    const previousValue = estimate?.invoiceDate
                                      ? moment(estimate.invoiceDate).format('YYYY-MM-DD')
                                      : null;
                                    if (nextValue === previousValue) return;
                                    void saveInvoicePatch(estimate, { invoiceDate: nextValue });
                                  }}
                                  className="rounded-md border-0 p-[12px]"
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </TableCell>

                        <TableCell className="px-3" onClick={(event) => event.stopPropagation()}>
                          {estimate?.qbEstimateId && estimate?.qbEstimateNumber ? (
                            <div className="flex flex-col">
                              <div className="inline-flex items-center gap-1">
                                <QuickBook width={14} height={14} />
                                <a
                                  href={qbUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[#3B97B1] hover:underline"
                                  data-qa={`client-estimate-row-${estimate.id}-qbo-link`}
                                >
                                  #{estimate.qbEstimateNumber}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                              <span className="text-xs text-muted-foreground">{qbDate}</span>
                            </div>
                          ) : (
                            <span>—</span>
                          )}
                        </TableCell>

                        <TableCell className="px-3" onClick={(event) => event.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                disabled={isActionLoading}
                                data-qa={`client-estimate-row-${estimate.id}-actions-trigger`}
                              >
                                {isActionLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MoreHorizontal className="h-4 w-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-[220px]">
                              <DropdownMenuItem
                                data-qa={`client-estimate-row-${estimate.id}-view-trigger`}
                                onSelect={() => handleView(estimate)}
                              >
                                <span className="inline-flex items-center gap-2">
                                  <Eye className="h-4 w-4" />
                                  View
                                </span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`client-estimate-row-${estimate.id}-send-update-trigger`}
                                onSelect={() => handleSendUpdate(estimate)}
                              >
                                <span className="inline-flex items-center gap-2">
                                  <Send className="h-4 w-4" />
                                  Send Update
                                </span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`client-estimate-row-${estimate.id}-edit-trigger`}
                                onSelect={() => handleEdit(estimate)}
                              >
                                <span className="inline-flex items-center gap-2">
                                  <Pencil className="h-4 w-4" />
                                  Edit
                                </span>
                              </DropdownMenuItem>
                              <EstimateHistory
                                dotMenu
                                singleUser={singleUser}
                                estimateId={estimate?.id}
                              />
                              <DropdownMenuItem
                                data-qa={`client-estimate-row-${estimate.id}-export-qbo-trigger`}
                                onSelect={(event) => {
                                  if (!qboConnected) {
                                    event.preventDefault();
                                    return;
                                  }
                                  void handleExportQuickBooks(estimate);
                                }}
                                className={!qboConnected ? 'opacity-60 cursor-default' : ''}
                              >
                                <span className="inline-flex w-full items-center gap-2">
                                  <Upload className="h-4 w-4" />
                                  {!qboConnectionChecked
                                    ? 'Checking QuickBooks...'
                                    : qboConnected
                                      ? estimate?.qbEstimateId
                                        ? 'Update in QuickBooks'
                                        : 'Export to QuickBooks'
                                      : 'Connect to QuickBooks'}
                                  {qboConnectionChecked && !qboConnected && (
                                    <QuickBooksConnectTooltip />
                                  )}
                                </span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`client-estimate-row-${estimate.id}-delete-trigger`}
                                variant="destructive"
                                onSelect={() => setDeleteTarget(estimate)}
                              >
                                <span className="inline-flex items-center gap-2">
                                  <Trash2 className="h-4 w-4" />
                                  Delete
                                </span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <AlertDialog
            open={!!deleteTarget}
            onOpenChange={(open) => !open && setDeleteTarget(null)}
          >
            <AlertDialogContent className="md:min-w-[560px]">
              <AlertDialogHeader className="gap-0">
                <AlertDialogTitle className="text-lg font-semibold m-0">
                  Delete Estimate
                </AlertDialogTitle>
                <AlertDialogDescription className="text-sm text-muted-foreground mt-2">
                  Are you sure you want to delete this estimate? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setDeleteTarget(null)}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => void handleDeleteConfirm()} variant={'delete'}>
                  {actionLoadingId === deleteTarget?.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <EstimateBuilderSheet
          open={builderState.open}
          initialMode={builderState.mode}
          initialDialog={builderState.initialDialog}
          estimateId={builderState.estimateId}
          clientId={clientId}
          accountId={accountId}
          client={client}
          account={account}
          accountUser={accountUser}
          qboConnected={qboConnected}
          existingEstimates={rows}
          onClose={closeBuilder}
          onChanged={handleEstimatingChanged}
          singleUser={singleUser}
        />

        <EstimateModal
          accountId={Number(accountId || localStorage.getItem('accountId') || 0)}
          isModalOpen={exportSuccessModalOpen}
          estimateId={exportedQbEstimateId || ''}
          qbEstimateDocNumber={exportedQbEstimateDocNumber || ''}
          onClose={() => setExportSuccessModalOpen(false)}
        />
        <BundleSetupModal
          uiActive={bundleSetupModalOpen}
          uiOnRequestClose={handleCloseBundleSetupModal}
          uiCloseOnEsc
          uiCloseOnOutsideClick
          onRetry={handleRetryBundleExport}
          onExportWithoutBundling={handleExportWithoutBundling}
          missingItems={bundleSetupMissingItems}
        />
      </>
    );
  }
);

export default ClientEstimating;
